<template>
  <div ref="root">
    <div>
      <GhostButton
        class="tw:flex tw:items-center tw:gap-2"
        @click="toggleFlyoutFilters"
        :class="{ 'btn-active': isFlyoutFiltersOpen }"
      >
        <FiltersIcon class="filter tw:fill-gray-800 tw:size-[18px]" />
        {{ $t('filters') }}
        <span
          v-if="selectedFiltersAmount > 0"
          class="filter-badge tw:bg-blue-700 tw:text-white tw:rounded-full tw:w-[20px] tw:h-[20px] tw:text-sm"
        >
          {{ selectedFiltersAmount }}
        </span>
      </GhostButton>
    </div>
    <div class="tw:relative">
      <div
        v-if="isFlyoutFiltersOpen"
        class="tw:absolute tw:top-[6px] tw:z-39 modal-filters tw:mx-auto tw:w-full tw:sm:min-w-[480px] tw:md:min-w-[740px] tw:landing:max-h-[500px] tw:bg-white tw:m-auto tw:rounded-lg box-shadow"
      >
        <div class="tw:relative tw:flex tw:flex-row tw:mt-0 tw:min-h-[500px]">
          <div class="tw:flex tw:border-r tw:border-gray-300 tw:p-5 tw:min-w-[260px] tw:flex-col tw:max-h-[500px]">
            <button
              v-for="filter in currentFilters"
              :key="filter.value"
              class="tw:flex tw:w-full tw:font-bold tw:px-4 tw:py-3 tw:mb-1 tw:rounded-lg tw:text-left"
              :class="
                filter.value === activeItem
                  ? 'tw:bg-blue-200 tw:text-blue-700'
                  : 'tw:hover:bg-gray-200 tw:hover:text-gray-800 tw:text-gray-600'
              "
              @click="scrollToElement(filter.value)"
            >
              {{ $t(filter.label) }}
            </button>
          </div>

          <div
            :key="tempFiltersArray"
            class="tw:flex tw:flex-col tw:px-8 tw:h-full tw:h-[calc(100vh-150px)] tw:w-full tw:max-h-[420px] tw:landing:h-[450px] tw:overflow-auto"
          >
            <MobileSelectPropertyFilter
              type="flyout"
              ref="properties"
              :label="$t('hotels')"
              :options="propertiesOptions"
              name="properties"
              :value="propertyTempFiltersArray"
              @onSelect="onSelectTempPropertyFilters"
              class="tw:pt-6"
            />
            <div class="tw:border-t tw:border-gray-200 tw:h-[1px] tw:w-full tw:my-3" />
            <MobileSelectStatusFilter
              ref="status"
              :label="$t('tbs_invoice_status')"
              :options="statusOptions"
              name="status"
              :value="statusTempFiltersArray"
              @onSelect="onSelectTempStatusFilters"
            />
          </div>
        </div>

        <div
          class="footer shadow tw:absolute tw:bottom-0 tw:w-full tw:flex tw:flex-row tw:justify-between tw:items-center tw:py-4 tw:px-8 tw:text-gray-800 tw:bg-white tw:rounded-b-lg"
        >
          <div class="tw:flex tw:w-full tw:justify-between tw:items-center">
            <div>
              <button
                :disabled="selectedFiltersAmount.length === 0"
                class="tw:disabled:text-gray-400 tw:font-bold"
                @click="resetAllFilters"
              >
                {{ $t('rc_filter_reset') }}
              </button>
            </div>
            <MainButton size="medium" @click="onApply">{{ $t('apply') }}</MainButton>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script setup>
import { onDeactivated, onMounted, ref } from 'vue'

import MainButton from '@/components/base/buttons/MainButton.vue'
import MobileSelectStatusFilter from '@/components/billing/premiumProducts/filters/MobileSelectStatusFilter.vue'
import MobileSelectPropertyFilter from '@/components/billing/premiumProducts/filters/MobileSelectPropertyFilter.vue'
import GhostButton from '@/components/base/buttons/GhostButton.vue'
import FiltersIcon from '@/components/icons/FiltersIcon.svg?component'

import { usePremiumProductsFilters } from '@/components/billing/premiumProducts/usePremiumProductsFilters'

const { isFlyoutFiltersOpen, openFlyoutFilters, closeFlyoutFilters, selectedFiltersAmount } =
  usePremiumProductsFilters()

const toggleFlyoutFilters = () => {
  if (isFlyoutFiltersOpen.value) closeFlyoutFilters()
  else openFlyoutFilters()
}

const props = defineProps({
  currentFilters: Array,
  propertiesOptions: Array,
  propertiesValue: Array,
  statusOptions: Array,
  statusValue: Array,
})

const root = ref(null)
const emit = defineEmits(['onApply'])
const closeFilters = () => {
  closeFlyoutFilters()
  activeItem.value = props.currentFilters[0].value
}

const propertyTempFiltersArray = ref([...props.propertiesValue])
const statusTempFiltersArray = ref([...props.statusValue])

const resetTempFilters = () => {
  propertyTempFiltersArray.value = [...props.propertiesValue]
  statusTempFiltersArray.value = [...props.statusValue]
}

const onApply = () => {
  emit('applyFilters', {
    property: propertyTempFiltersArray.value,
    status: statusTempFiltersArray.value,
  })
}
const resetAllFilters = () => {
  resetTempFilters()
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})
onDeactivated(() => {
  document.removeEventListener('click', handleClickOutside)
})
const handleClickOutside = evt => {
  if (root?.value !== null && !root?.value?.contains(evt.target)) {
    onClickAwayFn()
  }
}

const onClickAwayFn = () => {
  closeFilters()
  activeItem.value = props.currentFilters[0].value
}

const properties = ref(null)
const status = ref(null)
const activeItem = ref(props.currentFilters[0].value)

const scrollToElement = value => {
  activeItem.value = value
  if (value === 'properties')
    properties.value?.$el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' })
  if (value === 'status') status.value?.$el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' })
}

const onSelectTempPropertyFilters = newValue => {
  const isAllOption = newValue.id === 0
  const currentHasAll = propertyTempFiltersArray.value.some(item => item.id === 0)

  if (isAllOption) {
    propertyTempFiltersArray.value = [newValue]
  } else {
    if (currentHasAll) {
      propertyTempFiltersArray.value = propertyTempFiltersArray.value.filter(item => item.id !== 0)
    }

    const index = propertyTempFiltersArray.value.findIndex(item => item.id === newValue.id)
    if (index > -1) {
      propertyTempFiltersArray.value.splice(index, 1)

      if (propertyTempFiltersArray.value.length === 0) {
        propertyTempFiltersArray.value = [{ value: 'all', label: 'campaign_subscription_status_all', id: 0 }]
      }
    } else {
      propertyTempFiltersArray.value.push(newValue)
    }
  }
}

const isStatusSelected = itemId => {
  return statusTempFiltersArray.value.some(item => item.id === itemId)
}

const onSelectTempStatusFilters = selection => {
  if (selection.group) {
    const { options } = selection
    const isAnyGroupItemSelected = options.some(opt => isStatusSelected(opt.id))

    if (isAnyGroupItemSelected) {
      const groupItemIds = options.map(opt => opt.id)
      statusTempFiltersArray.value = statusTempFiltersArray.value.filter(item => !groupItemIds.includes(item.id))
      statusTempFiltersArray.value = statusTempFiltersArray.value.filter(item => item.id !== 'all')
    } else {
      statusTempFiltersArray.value = statusTempFiltersArray.value.filter(item => item.id !== 'all')
      options.forEach(option => {
        statusTempFiltersArray.value.push(option)
      })
    }
    return
  }

  const newValue = selection
  const isAllOption = newValue.id === 'all'
  if (isAllOption) {
    const allIsCurrentlySelected = statusTempFiltersArray.value.some(item => item.id === 'all')

    if (allIsCurrentlySelected) {
      statusTempFiltersArray.value = []
    } else {
      statusTempFiltersArray.value = []
      statusTempFiltersArray.value.push(newValue)

      props.statusOptions.forEach(option => {
        if (!option.group && option.id !== 'all') {
          statusTempFiltersArray.value.push(option)
        } else if (option.group) {
          statusTempFiltersArray.value.push(...option.options)
        }
      })
    }
  } else {
    const currentlySelected = isStatusSelected(newValue.id)

    if (currentlySelected) {
      statusTempFiltersArray.value = statusTempFiltersArray.value.filter(item => item.id !== newValue.id)
      statusTempFiltersArray.value = statusTempFiltersArray.value.filter(item => item.id !== 'all')
    } else {
      statusTempFiltersArray.value = statusTempFiltersArray.value.filter(item => item.id !== 'all')
      statusTempFiltersArray.value.push(newValue)
    }
  }
}
</script>
<style scoped>
.box-shadow {
  box-shadow: 0px 10px 30px 0px #293336bf;
}
.modal-filters-booking {
  left: 50%;
  transform: translateX(-30%);
}

.btn-active {
  background-color: var(--tw-color-gray-600);
  color: var(--tw-color-white);
}
button:hover .filter,
.btn-active .filter {
  stroke: var(--tw-color-white);
  fill: var(--tw-color-white);
}

button:hover .filter-badge,
.btn-active .filter-badge {
  background-color: var(--tw-color-white);
  color: var(--tw-color-blue-700);
}
</style>
